import { getApiError } from "@entrilia/shared/api/axiosHelper";
import { logError } from "@entrilia/shared/logging";
import { deduplicatePendingRequests } from "@entrilia/shared/utilities/promiseHelper";
import biApi from "../../../../../api/biApi";

interface PreviewRequest {
  artifactId: string;
  version: number;
}

const MAX_CACHED_PREVIEWS = 100;

const previewUrls = new Map<string, string>();

export const getArtifactPreviewKey = (artifactId: string, version: number): string => `${artifactId}:${version}`;

const removePreviewUrl = (key: string): void => {
  const url = previewUrls.get(key);
  if (url) {
    URL.revokeObjectURL(url);
    previewUrls.delete(key);
  }
};

const addPreviewUrl = (key: string, url: string): void => {
  previewUrls.set(key, url);
  for (const oldestKey of previewUrls.keys()) {
    if (previewUrls.size <= MAX_CACHED_PREVIEWS) {
      return;
    }
    removePreviewUrl(oldestKey);
  }
};

const fetchPreviewUrl = deduplicatePendingRequests(
  async ({ artifactId, version }: PreviewRequest): Promise<string | undefined> => {
    try {
      const image = await biApi.smartViewArtifacts.getPreview(artifactId, version);
      if (!image) {
        return undefined;
      }
      const url = URL.createObjectURL(image);
      addPreviewUrl(getArtifactPreviewKey(artifactId, version), url);
      return url;
    } catch (error) {
      if (!getApiError(error).isCanceledRequest) {
        logError(error, "[artifactPreviewCache] getPreview");
      }
      throw error;
    }
  },
  ({ artifactId, version }: PreviewRequest) => getArtifactPreviewKey(artifactId, version)
);

export const loadArtifactPreview = (artifactId: string, version: number): Promise<string | undefined> => {
  const key = getArtifactPreviewKey(artifactId, version);
  const url = previewUrls.get(key);
  if (!url) {
    return fetchPreviewUrl({ artifactId, version });
  }
  previewUrls.delete(key);
  previewUrls.set(key, url);
  return Promise.resolve(url);
};
